import {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useCallback,
  useState,
} from 'react';
import {
  content_v1_DatasourcePreset,
  core_v1_DataSource,
} from '@antimatterhq/asl-api-ts-v1';
import { useNavigate } from 'react-router-dom';
import { useImmer } from 'use-immer';
import { produce } from 'immer';
import { AxiosError } from 'axios';
import { datadogLogs } from '@datadog/browser-logs';
import { FormikContextType } from 'formik';
import { useCreateDataSource } from '@/hooks/core/useCreateDataSource';
import { useUpdateDataSource } from '@/hooks/core/useUpdateDataSource';
import {
  autoloaderApply,
  autoloaderCheck,
  bumpVersion,
} from '@/lib/datasource';
import {
  getUnpublishedChanges,
  isCompleted,
  setUnpublishedChanges,
} from '@/lib/datasource/uiStatus';
import { DATADOG_MESSAGE, getDatadogDatasource } from '@/utils/datadogUtils';
import { useOptimisticUpdateGetDataSource } from '@/hooks/core/useGetDataSource';

export type DatasourceSubmitType = 'next' | 'review' | 'previous' | '';

export type ConfigDataSourceProviderProps = {
  children: ReactNode;
  dataSource: core_v1_DataSource;
  isEdit: boolean;
  currentStep: StepId;
  preset?: content_v1_DatasourcePreset;
};

export type FormikType = Pick<
  FormikContextType<unknown>,
  'handleSubmit' | 'dirty'
>;

type ConfigDataSourceContextType = {
  handleNextStep: (name?: string | null) => void;
  handlePreviousStep: () => void;
  handleReview: () => void;
  dataSource: core_v1_DataSource;
  baseDatasource: core_v1_DataSource;
  save: (
    dataSource: core_v1_DataSource,
  ) => Promise<core_v1_DataSource | undefined>;
  isSaving: boolean;
  handleGoToStep: (stepId: StepId) => void;
  currentStep: StepId;
  isEdit: boolean;
  preset: content_v1_DatasourcePreset | undefined;
  isReviewButtonVisible?: boolean;
  publishChanges: () => Promise<void>;
  responseError?: AxiosError<{ message: string }>;
  formik?: FormikType;
  setFormik: (formik?: FormikType) => void;
};

const ConfigDataSourceContext = createContext<
  ConfigDataSourceContextType | undefined
>(undefined);

export const STEPS = [
  {
    id: 'source',
    name: 'Source',
    title: 'Add datasource',
    titleBelow: '',
  },
  {
    id: 'getting-started',
    name: 'Data',
    title: 'Add datasource',
    titleBelow: '',
  },
  {
    id: 'bronze',
    name: 'Bronze',
    title: 'Add datasource',
    titleBelow: <span className="text-bronze">Bronze table</span>,
  },
  {
    id: 'silver-pre-transform',
    name: 'Pre-transform',
    title: 'Add datasource',
    titleBelow: <span className="text-silver">Silver pre-transform</span>,
  },
  {
    id: 'silver',
    name: 'Silver',
    title: 'Add datasource',
    titleBelow: <span className="text-silver">Silver table</span>,
  },
  {
    id: 'gold',
    name: 'Gold',
    title: 'Add datasource',
    titleBelow: <span className="text-gold">Gold table</span>,
  },
  {
    id: 'review',
    name: 'Review',
    title: 'Review datasource',
    titleBelow: '',
  },
] as const;

export type StepId = (typeof STEPS)[number]['id'];

export function ConfigDataSourceProvider({
  dataSource: initialDataSource,
  currentStep,
  isEdit,
  children,
  preset,
}: ConfigDataSourceProviderProps) {
  const navigate = useNavigate();

  const optimisticUpdate = useOptimisticUpdateGetDataSource();
  const { mutateAsync: createDataSource, isPending: isCreating } =
    useCreateDataSource();
  const { mutateAsync: updateDataSource, isPending: isUpdating } =
    useUpdateDataSource();
  const isSaving = isCreating || isUpdating;
  const [responseError, setResponseError] = useState<
    AxiosError<{ message: string }> | undefined
  >(undefined);

  const [baseDatasource, setBaseDatasource] =
    useImmer<core_v1_DataSource>(initialDataSource);
  const [formik, setFormik] = useState<FormikType | undefined>(undefined);

  const dataSource = useMemo(
    () => getUnpublishedChanges(baseDatasource) || baseDatasource,
    [baseDatasource],
  );

  const completed = useMemo(() => isCompleted(dataSource), [dataSource]);

  // We don't want to show the review button if the user is adding a new datasource
  // and on the source or getting started steps
  const isReviewable = useMemo(
    () => completed || !['source', 'getting-started'].includes(currentStep),
    [currentStep, completed],
  );

  const applyDatasourceChanges = useCallback(
    (newDataSource: core_v1_DataSource) => {
      if (!completed) {
        return bumpVersion(newDataSource);
      }
      const source = setUnpublishedChanges(baseDatasource, newDataSource);
      return bumpVersion(source);
    },
    [completed, baseDatasource],
  );

  const update = useCallback(
    (updatedDatasource: core_v1_DataSource) => {
      const cleanedDatasource = autoloaderCheck(updatedDatasource);
      // eslint-disable-next-line no-console
      console.info('updated!', JSON.stringify(cleanedDatasource, null, 2));
      setResponseError(undefined);
      return updateDataSource(
        {
          name: cleanedDatasource?.metadata?.name || '',
          data: cleanedDatasource,
        },
        {
          onSuccess: async ({ data }) => {
            optimisticUpdate({
              data,
              name: cleanedDatasource?.metadata?.name || '',
            });
            setBaseDatasource(autoloaderApply(data));
            datadogLogs.logger.info(
              DATADOG_MESSAGE.DATASOURCE.UPDATE.STEP.SUCCESS,
              getDatadogDatasource(cleanedDatasource),
            );
            if (currentStep === 'review') {
              datadogLogs.logger.info(
                DATADOG_MESSAGE.DATASOURCE.UPDATE.COMPLETE.SUCCESS,
                getDatadogDatasource(cleanedDatasource),
              );
            }
            if (currentStep === 'review') {
              navigate('/configure/data-sources');
            }
          },
          onError: async ({ error }) => {
            datadogLogs.logger.error(
              DATADOG_MESSAGE.DATASOURCE.UPDATE.STEP.FAILURE,
              getDatadogDatasource(cleanedDatasource),
              error,
            );
            if (currentStep === 'review') {
              datadogLogs.logger.error(
                DATADOG_MESSAGE.DATASOURCE.UPDATE.COMPLETE.FAILURE,
                getDatadogDatasource(cleanedDatasource),
                error,
              );
            }
            setResponseError(error as AxiosError<{ message: string }>);
          },
          toasts: {
            success: 'Saved',
            error: 'There was an error saving the datasource',
          },
        },
      );
    },
    [
      updateDataSource,
      setBaseDatasource,
      currentStep,
      navigate,
      optimisticUpdate,
    ],
  );

  const create = useCallback(
    (newDatasource: core_v1_DataSource) => {
      const cleanedDatasource = autoloaderCheck(newDatasource);
      // eslint-disable-next-line no-console
      console.info('created!', JSON.stringify(cleanedDatasource, null, 2));
      setResponseError(undefined);
      return createDataSource(
        { data: cleanedDatasource },
        {
          onSuccess: async ({ data }) => {
            optimisticUpdate({
              data,
              name: cleanedDatasource?.metadata?.name || '',
            });
            datadogLogs.logger.info(
              DATADOG_MESSAGE.DATASOURCE.CREATE.STEP.SUCCESS,
              getDatadogDatasource(cleanedDatasource),
            );
            if (currentStep === 'review') {
              datadogLogs.logger.info(
                DATADOG_MESSAGE.DATASOURCE.CREATE.COMPLETE.SUCCESS,
                getDatadogDatasource(cleanedDatasource),
              );
            }
            setBaseDatasource(autoloaderApply(data));
          },
          onError: async ({ error }) => {
            datadogLogs.logger.error(
              DATADOG_MESSAGE.DATASOURCE.CREATE.STEP.FAILURE,
              getDatadogDatasource(cleanedDatasource),
              error,
            );
            if (currentStep === 'review') {
              datadogLogs.logger.error(
                DATADOG_MESSAGE.DATASOURCE.CREATE.COMPLETE.FAILURE,
                getDatadogDatasource(cleanedDatasource),
                error,
              );
            }
            setResponseError(error as AxiosError<{ message: string }>);
          },
          toasts: {
            success: 'Saved',
            error: 'There was an error saving the datasource',
          },
        },
      );
    },
    [createDataSource, setBaseDatasource, currentStep, optimisticUpdate],
  );

  const save = useCallback(
    async (newDataSource: core_v1_DataSource) => {
      // we can't update or create if we don't have a name
      if (!newDataSource?.metadata?.name) {
        setBaseDatasource(newDataSource);
        return newDataSource;
      }

      if (isEdit) {
        return update(applyDatasourceChanges(newDataSource));
      } else {
        return create(newDataSource);
      }
    },
    [create, update, setBaseDatasource, isEdit, applyDatasourceChanges],
  );

  const publishChanges = useCallback(async () => {
    // we can't use bumpVersion here because we need the version from the dataSourceState
    const newDataSource = produce(dataSource, (draft) => {
      if (!draft.metadata) {
        draft.metadata = {
          name: '',
        };
      }
      draft.metadata = {
        ...draft.metadata,
        version: (baseDatasource.metadata?.version ?? 0) + 1,
      };
    });
    await update(newDataSource);
  }, [update, dataSource, baseDatasource]);

  const goToStep = useCallback(
    (stepId: StepId, datasourceName?: string | null) => {
      setResponseError(undefined);
      const name = datasourceName || dataSource?.metadata?.name;
      if (isEdit || name) {
        navigate(`/configure/data-sources/edit/${name}/${stepId}`);
      } else {
        // Still in add mode (no datasource created yet), Preserve query params when navigating to maintain preset configuration
        const currentSearch = window.location.search;
        navigate(`/configure/data-sources/add/${stepId}${currentSearch}`);
      }
    },
    [dataSource, navigate, isEdit],
  );

  const handleNextStep = useCallback(
    async (name?: string | null) => {
      const stepIndex = STEPS.findIndex(({ id }) => id === currentStep);
      if (stepIndex === -1 || stepIndex === STEPS.length - 1) {
        return;
      }

      goToStep(STEPS[stepIndex + 1].id, name);
    },
    [currentStep, goToStep],
  );

  const handlePreviousStep = useCallback(() => {
    const stepIndex = STEPS.findIndex(({ id }) => id === currentStep);
    if (stepIndex < 1) {
      navigate('/configure/data-sources');
      return;
    }
    goToStep(STEPS[stepIndex - 1].id);
  }, [currentStep, goToStep, navigate]);

  const handleGoToStep = useCallback(
    (stepId: StepId) => {
      const stepIndex = STEPS.findIndex(({ id }) => id === stepId);
      if (stepIndex === -1) {
        return;
      }
      goToStep(STEPS[stepIndex].id);
    },
    [goToStep],
  );

  const handleReview = useCallback(async () => {
    goToStep('review');
  }, [goToStep]);

  return (
    <ConfigDataSourceContext.Provider
      value={{
        handleNextStep,
        handlePreviousStep,
        handleGoToStep,
        handleReview,
        save,
        isSaving,
        dataSource,
        baseDatasource,
        currentStep,
        isEdit,
        preset,
        isReviewButtonVisible: isReviewable,
        publishChanges,
        responseError,
        formik,
        setFormik,
      }}
    >
      {children}
    </ConfigDataSourceContext.Provider>
  );
}

export function useConfigDataSource() {
  const context = useContext(ConfigDataSourceContext);
  if (!context) {
    throw new Error(
      'useConfigDataSource must be used within a ConfigDataSourceProvider',
    );
  }
  return context;
}
